import dayjs from 'dayjs'

import { formatDateCustom } from 'src/lib/formatters'
import { knownPlatformLabel } from 'src/lib/voeProvider.utils'

export interface OutreachScheduleStep {
  type: string
  scheduledAt: string | Date
  /** Null for a step automation has not created yet. */
  status?: string | null
}

export interface OutreachPreviewSender {
  fullName: string
  email?: string | null
  phone?: string | null
}

export type OutreachPanelState =
  | 'NONE'
  | 'READY'
  | 'RUNNING'
  | 'ENDED'
  | 'BLOCKED'

export type OutreachBlockedReason =
  | 'NO_EMAIL_STEP'
  | 'CLOSED'
  | 'PENDING_REVIEW'
  | 'DO_NOT_CONTACT'
  | 'NOT_A_CARRIER'
  | 'CURRENT_EMPLOYER'
  | 'PLATFORM_ONLY'
  | 'OUT_OF_WINDOW'
  | 'AWAITING_MANUAL'
  | 'AWAITING_RULE'

export interface OutreachSchedulePreview {
  /** Absent from a server that predates panel states. */
  state?: OutreachPanelState | null
  blockedReason?: OutreachBlockedReason | null
  blockedPlatform?: string | null
  blockedWindowMonths?: number | null
  /** BLOCKED only: the recruiter can start automated outreach anyway. */
  overridable?: boolean | null
  automated: boolean
  started: boolean
  steps: OutreachScheduleStep[]
  nextRound?: OutreachScheduleStep[] | null
  sender?: OutreachPreviewSender | null
}

/** NONE keeps the manual composer; an older server says so with automated. */
export const opensPanel = (preview: OutreachSchedulePreview | null) =>
  preview?.state ? preview.state !== 'NONE' : Boolean(preview?.automated)

export const panelState = (
  preview: OutreachSchedulePreview
): Exclude<OutreachPanelState, 'NONE'> => {
  if (preview.state && preview.state !== 'NONE') return preview.state
  return preview.started ? 'RUNNING' : 'READY'
}

/** The panel's one line on why automation won't email this employer. */
export const blockedReasonLine = (
  reason: OutreachBlockedReason | null | undefined,
  employerName: string,
  platform?: string | null,
  windowMonths?: number | null
): string => {
  switch (reason) {
    case 'DO_NOT_CONTACT':
      return "Automation won't email this employer: the applicant didn't consent to contacting them."
    case 'PLATFORM_ONLY':
      return `${employerName} only verifies through ${
        knownPlatformLabel(platform) ?? 'its verification platform'
      }.`
    case 'NOT_A_CARRIER':
      return "This employer isn't a DOT carrier, so automation doesn't email it."
    case 'PENDING_REVIEW':
      return 'The employer answered and the response is waiting for your review, so automation stopped.'
    case 'CURRENT_EMPLOYER':
      return 'Your settings skip employers the applicant still works for. You can start automation for this one anyway.'
    case 'OUT_OF_WINDOW':
      return `This job ended more than ${
        windowMonths ?? 36
      } months ago, outside your verification window. You can start automation for it anyway.`
    case 'AWAITING_MANUAL':
      return 'Automated follow-ups start after your first outreach. Send it yourself, or start automation now.'
    case 'AWAITING_RULE':
      return 'Automation starts when your rule runs for this applicant. You can start it now.'
    case 'NO_EMAIL_STEP':
      return 'Your automated outreach has no email steps.'
    case 'CLOSED':
      return "This verification is closed, so automation won't email this employer."
    default:
      return "Automation won't email this employer."
  }
}

/** A not-yet-created step due within this window goes out on Start. */
const RIGHT_AWAY_MS = 60 * 1000

export const stepDateLabel = (
  step: OutreachScheduleStep,
  now: Date = new Date()
): string => {
  const at = dayjs(step.scheduledAt)
  if (step.status == null && at.valueOf() - now.getTime() <= RIGHT_AWAY_MS) {
    return 'Today, right away'
  }
  return formatDateCustom(at.toDate(), 'ddd, MMM D')
}

/** The VoE drawer's words for the same statuses. */
export const stepStatusLabel = (step: OutreachScheduleStep): string => {
  switch (step.status) {
    case 'COMPLETED':
      return 'Completed'
    case 'IN_PROGRESS':
      return 'In progress'
    case 'FAILED':
      return 'Failed'
    default:
      return 'Scheduled'
  }
}
